/**
 * 강사-학생 할당 모달 컴포넌트
 * - Transfer 컴포넌트로 학생 할당/해제
 * - 왼쪽: 미할당 학생, 오른쪽: 할당된 학생
 */

import React, { useState, useEffect } from 'react';
import { Modal, Transfer, message } from 'antd';
import type { TransferProps } from 'antd';
import { userService } from '../../services/userService';
import type { User } from '../../types/user';

interface AssignStudentsModalProps {
  open: boolean;
  instructor: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface StudentRecord {
  key: string;
  name: string;
  email: string;
  phone?: string;
}

export const AssignStudentsModal: React.FC<AssignStudentsModalProps> = ({
  open,
  instructor,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [allStudents, setAllStudents] = useState<StudentRecord[]>([]);
  const [assignedStudentIds, setAssignedStudentIds] = useState<string[]>([]);
  const [initialAssignedIds, setInitialAssignedIds] = useState<string[]>([]);

  /**
   * 전체 학생 목록 및 현재 할당된 학생 조회
   */
  const fetchData = async () => {
    if (!instructor) return;

    try {
      setLoading(true);

      // 전체 학생 목록 조회
      const allStudentsResponse = await userService.getUsers({
        role: 'student',
        page_size: 100, // 최대 100명의 학생 조회
      });

      // 현재 강사에게 할당된 학생 조회
      const assignedStudents = await userService.getInstructorStudents(instructor.id);

      const students: StudentRecord[] = allStudentsResponse.users.map((user) => ({
        key: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone,
      }));

      const assignedIds = assignedStudents.map((student) => student.id);

      setAllStudents(students);
      setAssignedStudentIds(assignedIds);
      setInitialAssignedIds(assignedIds);
    } catch (error) {
      message.error('학생 목록 조회 실패');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 모달 열릴 때 데이터 조회
   */
  useEffect(() => {
    if (open && instructor) {
      fetchData();
    }
  }, [open, instructor]);

  /**
   * 모달 닫기 처리
   */
  const handleClose = () => {
    setAllStudents([]);
    setAssignedStudentIds([]);
    setInitialAssignedIds([]);
    onClose();
  };

  /**
   * Transfer 변경 처리
   */
  const handleChange: TransferProps['onChange'] = (targetKeys) => {
    setAssignedStudentIds(targetKeys as string[]);
  };

  /**
   * 할당 변경 사항 저장
   */
  const handleSubmit = async () => {
    if (!instructor) return;

    try {
      setLoading(true);

      // 새로 할당된 학생 (이전에 없었는데 현재 있음)
      const toAssign = assignedStudentIds.filter(
        (id) => !initialAssignedIds.includes(id)
      );

      // 할당 해제된 학생 (이전에 있었는데 현재 없음)
      const toUnassign = initialAssignedIds.filter(
        (id) => !assignedStudentIds.includes(id)
      );

      // 할당 API 호출
      if (toAssign.length > 0) {
        await userService.assignStudentsToInstructor({
          instructor_id: instructor.id,
          student_ids: toAssign,
        });
      }

      // 할당 해제 API 호출
      if (toUnassign.length > 0) {
        await userService.unassignStudentsFromInstructor({
          instructor_id: instructor.id,
          student_ids: toUnassign,
        });
      }

      message.success('학생 할당이 변경되었습니다');
      handleClose();
      onSuccess();
    } catch (error: any) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('학생 할당 변경 실패');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`학생 할당 - ${instructor?.name || ''}`}
      open={open}
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={loading}
      okText="저장"
      cancelText="취소"
      width={700}
    >
      <Transfer
        dataSource={allStudents}
        titles={['미할당 학생', '할당된 학생']}
        targetKeys={assignedStudentIds}
        onChange={handleChange}
        render={(item) => `${item.name} (${item.email})`}
        listStyle={{
          width: 300,
          height: 400,
        }}
        showSearch
        filterOption={(inputValue, item) =>
          item.name.toLowerCase().includes(inputValue.toLowerCase()) ||
          item.email.toLowerCase().includes(inputValue.toLowerCase())
        }
        locale={{
          itemUnit: '명',
          itemsUnit: '명',
          searchPlaceholder: '검색',
          notFoundContent: '학생 없음',
        }}
      />
    </Modal>
  );
};
